import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Panel } from "../ui";

const SERIES = [
  { key: "needsCreated", color: "#1E4D78" },
  { key: "needsCovered", color: "#1BAE9A" },
  { key: "matchesProposed", color: "#D9822B" },
  { key: "matchesConfirmed", color: "#C93A45" },
];

const PilotGroupedChartCard = ({
  t,
  isLoading,
  chartData,
  totals,
  selectedMonthKey,
  onMonthChange,
  onDrillDown,
  isDrillDownDisabled,
  drillDownHelpText,
  chartGranularity = "month",
}) => {
  const hasData = chartData.some(
    (item) =>
      item.needsCreated > 0 ||
      item.needsCovered > 0 ||
      item.matchesProposed > 0 ||
      item.matchesConfirmed > 0
  );

  return (
    <Panel
      title={t("dashboard.pilotChart.title")}
      actions={isLoading ? <Spinner size="sm" color="portal.primary" /> : undefined}
    >
      <Box p="12px">
        <SimpleGrid columns={{ base: 2, xl: 4 }} spacing="10px" mb="12px">
          {SERIES.map((series) => (
            <Box
              key={series.key}
              borderWidth="1px"
              borderColor="portal.line"
              borderRadius="10px"
              bg="portal.panel"
              boxShadow="kpi"
              px="12px"
              py="10px"
            >
              <HStack spacing="6px" sx={{ marginBottom: "4px !important" }}>
                <Box w="9px" h="9px" borderRadius="2px" bg={series.color} flexShrink={0} />
                <Text
                  fontSize="11px"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                  color="portal.muted"
                  noOfLines={1}
                  sx={{ marginBottom: "0 !important" }}
                >
                  {t(`dashboard.pilotChart.legend.${series.key}`)}
                </Text>
              </HStack>
              <Text
                fontSize="19px"
                fontWeight="900"
                letterSpacing="-0.04em"
                color="portal.text"
                sx={{ marginBottom: "0 !important" }}
              >
                {totals[series.key]}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        {hasData ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDF2F5" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#617386" }} stroke="#D6E0EA" />
              <YAxis tick={{ fontSize: 11, fill: "#617386" }} stroke="#D6E0EA" allowDecimals={false} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {SERIES.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={t(`dashboard.pilotChart.legend.${series.key}`)}
                  fill={series.color}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Flex
            h="280px"
            borderRadius="10px"
            bg="portal.soft"
            borderWidth="1px"
            borderColor="portal.line"
            align="center"
            justify="center"
            px="24px"
          >
            <Text fontSize="12px" color="portal.muted" textAlign="center" sx={{ marginBottom: "0 !important" }}>
              {t("dashboard.pilotChart.empty")}
            </Text>
          </Flex>
        )}

        {chartGranularity === "month" && (
          <Box mt="16px">
            <Flex gap="8px" flexWrap="wrap" align="flex-end">
              <Box maxW={{ base: "full", md: "240px" }} flex="1">
                <Text
                  fontSize="11px"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                  color="portal.muted"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("dashboard.drilldown.monthLabel")}
                </Text>
                <Select
                  h="31px"
                  fontWeight="600"
                  value={selectedMonthKey}
                  onChange={(event) => onMonthChange(event.target.value)}
                  aria-label={t("dashboard.drilldown.monthLabel")}
                >
                  {chartData.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Button
                variant="primary"
                size="sm"
                h="31px"
                px="12px"
                fontSize="12px"
                onClick={onDrillDown}
                isDisabled={isDrillDownDisabled}
              >
                {t("dashboard.drilldown.button")}
              </Button>
            </Flex>

            {drillDownHelpText && (
              <Text fontSize="12px" color="portal.muted" mt="8px" sx={{ marginBottom: "0 !important" }}>
                {drillDownHelpText}
              </Text>
            )}
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export default PilotGroupedChartCard;
